import { useMemo } from 'react';
import type { Nullable } from '@direct-frontend/stdlib';
import { useField, useFormContext, useFormInstance } from '@adv-frontend/form-kit';
import { type OrderDeliveryMethod, OrderDeliveryMethods } from '@/shared/api/order';
import type { IntervalFieldValue } from '@/shared/components/IntervalInput';
import { ORDER_FORM_ID } from '../../constants';
import { orderForm } from '../../model/forms/orderForm';

/*
 * Возвращает доступные интервалы доставки и текущее значение интервала для указанного чанка.
 * Учитывает способ доставки (курьер / ПВЗ) и заполненность адреса.
 */
export const useChunkIntervals = (chunkId: number) => {
    const { fields } = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });
    const context = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const fieldDeliveryMethod = useField(fields.deliveryMethod);
    const fieldAddressCourier = useField(fields.addressCourier);
    const fieldAddressPickup = useField(fields.addressPickup);
    const isCourierDeliveryMethod = fieldDeliveryMethod.value === OrderDeliveryMethods.COURIER;
    const isPickupPointDeliveryMethod = fieldDeliveryMethod.value === OrderDeliveryMethods.PICKUP_POINT;
    const isCourierAddressFilled = Boolean(isCourierDeliveryMethod && fieldAddressCourier.value.address);
    const isPickupPointAddressFilled = Boolean(isPickupPointDeliveryMethod && fieldAddressPickup.value.pickupPointId);
    const fieldDeliveryPickupPointInterval = useField(fields.deliveryPickupPointInterval);
    const fieldDeliveryCourierInterval = useField(fields.deliveryCourierInterval);
    const isDeliveryAddressFilled = isCourierAddressFilled || isPickupPointAddressFilled;

    const deliveryIntervals = useMemo(() => {
        const deliveryMethod = fieldDeliveryMethod.value as Nullable<OrderDeliveryMethod>;

        if (!deliveryMethod) {
            return [];
        }

        const deliveryIntervalsChunks = context.deliveryIntervalsChunks?.[deliveryMethod] || [];

        return deliveryIntervalsChunks.find((chunk) => chunk.chunkId === chunkId)?.intervals || [];
    }, [chunkId, context.deliveryIntervalsChunks, fieldDeliveryMethod.value]);

    const currentInterval = useMemo<IntervalFieldValue>(() => {
        if (!isDeliveryAddressFilled) {
            return { type: 'notInterval' };
        }

        if (isPickupPointDeliveryMethod) {
            return fieldDeliveryPickupPointInterval.selectors.getValueByChunkId(chunkId);
        }

        if (isCourierDeliveryMethod) {
            return fieldDeliveryCourierInterval.selectors.getValueByChunkId(chunkId);
        }

        return { type: 'notInterval' };
    }, [
        chunkId,
        fieldDeliveryCourierInterval,
        fieldDeliveryPickupPointInterval,
        isCourierDeliveryMethod,
        isDeliveryAddressFilled,
        isPickupPointDeliveryMethod,
    ]);

    return {
        deliveryIntervals,
        currentInterval,
    };
};
